/**
 * Content registry — discovers article modules at build time via Vite's
 * `import.meta.glob` and serves them on demand.
 *
 * Convention:
 *   web/content/countries/<cc-lower>/<lang>/_hub.ts        — HubArticle
 *   web/content/countries/<cc-lower>/<lang>/<topic>.ts     — TopicArticle
 *
 * The `_hub` underscore-prefix avoids collision with the topic slug list.
 *
 * Each module's default export must satisfy `HubArticle` or `TopicArticle`.
 * The registry returns `null` when no module exists, signalling the page
 * renderer to fall back to the skeleton state.
 */

import type { CountryCode, LangCode, TopicSlug } from './architecture';
import type { HubArticle, TopicArticle } from './content-types';

type ArticleModule<T> = { default: T };

// Vite glob: dynamic imports keyed by the source-relative path.  Eager:false
// means each module ships in its own chunk (only loaded when its page mounts).
const HUB_MODULES = import.meta.glob<ArticleModule<HubArticle>>(
  '../../../content/countries/*/*/_hub.ts',
);

const TOPIC_MODULES = import.meta.glob<ArticleModule<TopicArticle>>(
  '../../../content/countries/*/*/*.ts',
);

function pathFor(cc: CountryCode, lang: LangCode, topic: TopicSlug | '_hub'): string {
  return `../../../content/countries/${cc.toLowerCase()}/${lang}/${topic}.ts`;
}

/**
 * Returns a module loader (or null when no module exists for this triple).
 * The loader is the Vite-generated dynamic-import function — call it to
 * load the article asynchronously.
 */
export function findHubLoader(
  cc: CountryCode,
  lang: LangCode,
): (() => Promise<ArticleModule<HubArticle>>) | null {
  const key = pathFor(cc, lang, '_hub');
  return HUB_MODULES[key] ?? null;
}

export function findTopicLoader(
  cc: CountryCode,
  lang: LangCode,
  topic: TopicSlug,
): (() => Promise<ArticleModule<TopicArticle>>) | null {
  const key = pathFor(cc, lang, topic);
  // The topic glob matches _hub.ts too; filter it out so a missing topic
  // doesn't fall through to the hub module.
  if (topic === ('_hub' as never)) return null;
  return TOPIC_MODULES[key] ?? null;
}

/**
 * Convenience helper for the build-script side: returns the list of
 * (cc, lang, topic) triples that have a topic article on disk.  Powers
 * the sitemap / prerender published-articles registry without manual edits.
 *
 * NOTE: not currently used by the JS build scripts (they read
 * `published-articles.mjs` directly).  Available for tests / admin UI.
 */
export function listPublishedTopics(): Array<{
  cc: string; lang: string; topic: string;
}> {
  const out: Array<{ cc: string; lang: string; topic: string }> = [];
  const re = /\/content\/countries\/([^/]+)\/([^/]+)\/([^/]+)\.ts$/;
  for (const key of Object.keys(TOPIC_MODULES)) {
    const m = key.match(re);
    if (!m) continue;
    const [, ccLower, lang, topic] = m;
    if (topic === '_hub') continue;
    out.push({ cc: ccLower.toUpperCase(), lang, topic });
  }
  return out;
}
